import { isObject } from 'lodash';

import { PickNonDist } from '@dotfile/shared/common';

import {
  AutomationInputCaseLogic,
  AutomationInputCompanyLogic,
  AutomationInputIndividualLogic,
} from '../../modules/automation';
import { JsonLogic } from '../../utils';
import { AutomationPipelineModel } from './automation-pipeline.model';
import { BaseModel } from './base-model';
import { CaseModel } from './case.model';
import { AssignmentRuleStrategyEnum } from './enum/assignment-rule-strategy.enum';
import { AutomationActionStatusEnum } from './enum/automation-action-status.enum';
import { AutomationActionTypeEnum } from './enum/automation-action-type.enum';
import { AutomationJobStatusEnum } from './enum/automation-job-status.enum';
import { AutomationResultStatusEnum } from './enum/automation-result-status.enum';
import { AutomationStageTypeEnum } from './enum/automation-stage-type.enum';
import { CaseFlagEnum } from './enum/case-flag.enum';
import { CheckTypeEnum } from './enum/check-type.enum';
import { ControlCompletionAggregateEnum } from './enum/control-completion-aggregate.enum';
import { ControlScopeEntityTypeEnum } from './enum/control-scope-entity-type.enum';
import { EntityTypeEnum } from './enum/entity-type.enum';
import { RiskLevelEnum } from './enum/risk-level.enum';
import { TemplateRuleTypeEnum } from './enum/template-rule-type.enum';
import { WorkspaceMemberModel } from './relations/workspace-member.model';
import { TemplateRuleCheckVariantModel } from './template-rule-check-variant.model';
import {
  TemplateRuleCheckModel,
  TemplateRuleControlModel,
} from './template-rule.model';
import { TemplateModel } from './template.model';
import { WorkspaceModel } from './workspace.model';

interface AutomationJobActionBaseModel<
  TOutput extends Record<string, unknown> | null = Record<
    string,
    unknown
  > | null,
> {
  type: AutomationActionTypeEnum;
  /**
   * Depends on each action type
   * @see [libs/shared/domain/src/lib/modules/automation/automation-pipeline/actions.ts](https://gitlab.com/dotfile/dotfile/-/blob/master/libs/shared/domain/src/lib/modules/automation/automation-pipeline/actions.ts)
   */
  parameters: unknown[];
  status: AutomationActionStatusEnum;
  /**
   * Set with `AutomationJobStatusEnum.failed` or `AutomationJobStatusEnum.skipped`
   */
  message?: string;

  /**
   * Set with `AutomationJobStatusEnum.success`
   */
  output?: TOutput;
}

export interface AutomationJobActionPendingModel
  extends AutomationJobActionBaseModel {
  status: typeof AutomationActionStatusEnum.pending;
  message?: never;
}

export interface AutomationJobActionSuccessModel<
  TOutput extends Record<string, unknown> | null = Record<
    string,
    unknown
  > | null,
> extends AutomationJobActionBaseModel {
  status: typeof AutomationActionStatusEnum.success;
  message?: never;
  output: TOutput;
}

export interface AutomationJobActionFailedModel
  extends AutomationJobActionBaseModel {
  status: typeof AutomationActionStatusEnum.failed;
  message: string;
  output?: never;
}

export interface AutomationJobActionSkippedModel
  extends AutomationJobActionBaseModel {
  status: typeof AutomationActionStatusEnum.skipped;
  message?: string;
  output?: never;
}

export type AutomationJobActionModel<
  TOutput extends Record<string, unknown> | null = Record<
    string,
    unknown
  > | null,
> =
  | AutomationJobActionPendingModel
  | AutomationJobActionFailedModel
  | AutomationJobActionSkippedModel
  | AutomationJobActionSuccessModel<TOutput>;

interface AutomationJobResultBaseModel {
  /**
   * Set when a logic was evaluated during this job execution
   */
  logic_match?: boolean;

  /**
   * Status of this job execution for code, logic or other depending on the stage type
   * (excluding actions)
   */
  status: AutomationResultStatusEnum;
  /**
   * * Mostly set with `failed` or `skipped`
   */
  message?: string;
}

export interface AutomationJobResultSuccessModel
  extends AutomationJobResultBaseModel {
  status: typeof AutomationResultStatusEnum.success;
  message?: never;
}

export interface AutomationJobResultFailedOrSkippedModel
  extends AutomationJobResultBaseModel {
  status:
    | typeof AutomationResultStatusEnum.failed
    | typeof AutomationResultStatusEnum.skipped;
  message?: string;
}

export type AutomationJobResultModel =
  | AutomationJobResultSuccessModel
  | AutomationJobResultFailedOrSkippedModel;

export interface AutomationJobBaseModel extends BaseModel {
  id: string;

  workspace?: WorkspaceModel;
  workspaceId: string;

  case?: CaseModel;
  caseId: string;

  pipeline?: AutomationPipelineModel;
  pipelineId: string;

  stageId: string;
  position: number;

  status: AutomationJobStatusEnum;
  input: Record<string, unknown>;

  stageType: AutomationStageTypeEnum;

  parameters: unknown; // More precise for each stage type

  result: AutomationJobResultModel | null;

  /**
   * Actions executed as part of this job
   */
  actions: AutomationJobActionModel[] | null;
  context: unknown; // More precise for each stage type

  startedAt: Date | null;
  endedAt: Date | null;
  deletedAt: Date | null;
}

export interface AutomationJobRiskModel extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.risk;

  parameters: { code: string };

  context: {
    riskEngineId: string;
    riskEngineVersionId: string;
  };
}

export interface AutomationJobClientPortalWorkflowModel
  extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.client_portal_workflow;

  parameters: { code: string };

  context: {
    clientPortalId: string;
    clientPortalVersionId: string;
  };
}

export interface AutomationJobClientPortalDefaultModel
  extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.client_portal_default;

  parameters: { templateId: string | null };

  context: {
    clientPortalId: string;
    clientPortalVersionId: string;
  };
}

export interface AutomationJobAssignmentModel extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.assignment;

  parameters: {
    logic: JsonLogic;
    poolMembers: Pick<WorkspaceMemberModel, 'userId' | 'suspendedAt'>[];
    strategy: AssignmentRuleStrategyEnum;
  };

  context: {
    assignmentRuleId: string;
  };
}

export interface AutomationJobTemplateActivationsBaseModel
  extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.template_activations;

  context: {
    /**
     * Template id if the case has a template attached, can be null for case without template
     */
    templateId: string | null;
  };
}

export type AutomationJobTemplateActivationsCheckRulesModel<
  T extends CheckTypeEnum = CheckTypeEnum,
> = PickNonDist<
  TemplateRuleCheckModel<T>,
  // ids are not exposed because it is meaningless since our current implementation of
  // template management delete and re-create all rules on save
  | 'checkType'
  | 'checkSubtype'
  | 'checkSettings'
  | 'checkTarget'
  | 'activationJsonLogic'
  | 'deduplication'
> & {
  /**
   * Variants of the rules, sorted by position
   */
  variants: PickNonDist<
    TemplateRuleCheckVariantModel<T>,
    'label' | 'activationJsonLogic' | 'checkSettings'
  >[];
};

export type AutomationJobTemplateActivationsControlRulesModel = Pick<
  TemplateRuleControlModel,
  // id is not exposed because it is meaningless since our current implementation of
  // template management delete and re-create all rules on save
  'controlId' | 'activationJsonLogic'
>;

export interface AutomationJobTemplateActivationsCheckModel
  extends AutomationJobTemplateActivationsBaseModel {
  input: {
    case: AutomationInputCaseLogic;
    // Exactly one of the company or individual is set, depending on the entityType
    company?: AutomationInputCompanyLogic;
    individual?: AutomationInputIndividualLogic;
  };

  parameters: {
    ruleType: typeof TemplateRuleTypeEnum.check;
    entityType: EntityTypeEnum;
    entityId: string;
    templateRules: AutomationJobTemplateActivationsCheckRulesModel[];
    mergeIdentityChecks: TemplateModel['mergeIdentityChecks'];
  };
}

export interface AutomationJobTemplateActivationsControlModel
  extends AutomationJobTemplateActivationsBaseModel {
  input: {
    case: AutomationInputCaseLogic;
    main_company: AutomationInputCompanyLogic;
  };

  parameters: {
    ruleType: typeof TemplateRuleTypeEnum.control;
    entityType?: never;
    entityId?: never;
    templateRules: AutomationJobTemplateActivationsControlRulesModel[];
  };
}

export type AutomationJobTemplateActivationsModel =
  | AutomationJobTemplateActivationsCheckModel
  | AutomationJobTemplateActivationsControlModel;

export interface AutomationJobControlModel extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.control;

  context: {
    controlId: string;
    caseControlId: string;
  };

  parameters:
    | AutomationJobControlParametersCode
    | AutomationJobControlParametersLogic;
}

export interface AutomationJobControlParametersCode {
  code: string;
}

export interface AutomationJobControlParametersLogic {
  scope: ControlScopeEntityTypeEnum;
  filterLogic: JsonLogic | null; // always null for scope=case, could be null for other scopes
  aggregate: ControlCompletionAggregateEnum | null; // always null for scope=case, always set for other scopes
  completionLogic: JsonLogic;
}

export interface AutomationJobTemplateDecisionApprovalModel
  extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.template_decision_approval;

  parameters: Pick<
    Partial<TemplateModel>,
    'caseAutoApproval' | 'caseAutoReopening'
  >;
  input: {
    case: {
      flags: CaseFlagEnum[];
      case_controls: { control_id: string; is_completed: boolean }[];
    };
  };

  context: {
    templateId: string;
  };
}

export interface AutomationJobTemplateDecisionRejectionModel
  extends AutomationJobBaseModel {
  stageType: typeof AutomationStageTypeEnum.template_decision_rejection;

  parameters: Pick<Partial<TemplateModel>, 'autoRejectProhibitedRisk'>;
  input: {
    case: {
      risk: {
        level: RiskLevelEnum;
      };
    };
  };

  context: {
    templateId: string;
  };
}

export type AutomationJobModel =
  | AutomationJobRiskModel
  | AutomationJobClientPortalWorkflowModel
  | AutomationJobClientPortalDefaultModel
  | AutomationJobAssignmentModel
  | AutomationJobTemplateActivationsModel
  | AutomationJobControlModel
  | AutomationJobTemplateDecisionApprovalModel
  | AutomationJobTemplateDecisionRejectionModel;

export const isAutomationJobStageType =
  <T extends AutomationStageTypeEnum>(stageType: T) =>
  (
    job: AutomationJobBaseModel,
  ): job is AutomationJobModel & { stageType: T } => {
    return job.stageType === stageType;
  };

// Type guard functions
export const isAutomationJobRisk = isAutomationJobStageType(
  AutomationStageTypeEnum.risk,
);
export const isAutomationJobClientPortalWorkflow = isAutomationJobStageType(
  AutomationStageTypeEnum.client_portal_workflow,
);
export const isAutomationJobClientPortalDefault = isAutomationJobStageType(
  AutomationStageTypeEnum.client_portal_default,
);
export const isAutomationJobAssignment = isAutomationJobStageType(
  AutomationStageTypeEnum.assignment,
);
export const isAutomationJobTemplateActivations = isAutomationJobStageType(
  AutomationStageTypeEnum.template_activations,
);
export const isAutomationJobControls = isAutomationJobStageType(
  AutomationStageTypeEnum.control,
);

export const isAutomationJobTemplateDecisionApproval = isAutomationJobStageType(
  AutomationStageTypeEnum.template_decision_approval,
);
export const isAutomationJobTemplateDecisionRejection =
  isAutomationJobStageType(AutomationStageTypeEnum.template_decision_rejection);

export const isAutomationJobTemplateActivationsCheck = (
  job: AutomationJobBaseModel,
): job is AutomationJobTemplateActivationsCheckModel =>
  isAutomationJobTemplateActivations(job) &&
  isObject(job.parameters) &&
  'ruleType' in job.parameters &&
  job.parameters.ruleType === TemplateRuleTypeEnum.check;

export const isAutomationJobTemplateActivationsControl = (
  job: AutomationJobBaseModel,
): job is AutomationJobTemplateActivationsControlModel =>
  isAutomationJobTemplateActivations(job) &&
  isObject(job.parameters) &&
  'ruleType' in job.parameters &&
  job.parameters.ruleType === TemplateRuleTypeEnum.control;
